import { z } from 'zod';
import { itemDefinitionIdSchema } from '../inventory/definitions';
import { huntSessionStateSchema, loadoutSchema, progressionSchema } from './hunt';

/**
 * Skins on the wire: one read and two slot moves (ADR 0036, ADR 0039).
 *
 * ## The free claim is gone
 *
 * ADR 0037 added `POST /api/skins/redeem`, which granted this account a free
 * copy. **It no longer exists**, along with its request schema, its response
 * schema and its route constant. Skins are obtained only by rolling the Cash
 * gacha, and this file carries no way to ask for one.
 *
 * The constants were DELETED rather than kept pointing somewhere harmless. An
 * alias that quietly answers is worse than a 404: a client calling the old path
 * would be told nothing changed. There is no route at `/api/skins/redeem`, so
 * the API's own fallback answers it - which is the honest reply, because the
 * operation is not "temporarily unavailable", it is gone.
 *
 * ## What a request may say
 *
 * An instance id to wear, a loadout version and a replay key. **Never** an
 * account id - that comes from the session and from nothing else - and never a
 * price, a currency, a rarity, an attribute or a result.
 *
 * ## What is deliberately absent
 *
 * No socket count and no jewel. A skin INSTANCE carries a socket capacity from
 * ADR 0040 onwards, but it is a property of the copy rather than of the slot,
 * and nothing in the loadout wire needs it. Naming one here would be a promise
 * about a mechanism that this contract has nothing to do with.
 */

export const SKINS_ROUTE = '/api/skins' as const;
export const SKIN_EQUIP_ROUTE = '/api/skins/equip' as const;
export const SKIN_UNEQUIP_ROUTE = '/api/skins/unequip' as const;

/**
 * Bumped when this response shape changes incompatibly.
 *
 * 2: `redeemable` was removed from `skinViewSchema` when the free claim was
 * retired (ADR 0039). A client drawing a claim button from that field would
 * have been drawing a button for a route that no longer answers.
 *
 * 3: **ownership stopped being a property of a definition** (ADR 0040, gate
 * V23). Version 2 answered with one entry per definition carrying a single
 * nullable `itemInstanceId`, which could not represent two copies at all - so a
 * player holding a 6-socket copy and a 1-socket copy of the same skin was told
 * about one of them, and *which* one depended on the order Postgres happened to
 * return rows in. The catalogue and the shelf are now two different lists, and
 * the shelf is a list of INSTANCES.
 *
 * 4: **a slot move answers with the run it reconfigured** (ADR 0042, gate
 * V23.2A.1). `HuntService.changeSkinSlot` has always settled the tail, moved
 * the slot and rewritten the running session's frozen loadout in one
 * transaction - and the answer threw all of that away and returned a shelf. A
 * client had no authoritative state to adopt, so the only way to see the skin
 * on the ship was a second `POST /api/hunt/bootstrap`: extra latency, a second
 * reconciliation, and a second chance to be wrong about a change that was
 * already decided.
 *
 * The bump is a real incompatibility in BOTH directions and is deliberately not
 * additive. {@link skinMutationResponseSchema} gained a REQUIRED `hunt`, so a
 * version-3 server's answer fails validation in a version-4 client rather than
 * being adopted with an arena nobody reconfigured; and a version-3 client
 * refuses a version-4 answer at the `contractVersion` literal rather than
 * silently ignoring the run it was handed.
 */
export const SKINS_CONTRACT_VERSION = 4;

/**
 * One skin DEFINITION: what the art is, independent of who owns a copy.
 *
 * Deliberately says nothing about this account. Ownership moved to
 * {@link ownedSkinSchema} because two copies of one definition are two
 * different things a player can equip, and a per-definition shape has nowhere
 * to put the second one.
 */
export const skinViewSchema = z.object({
  definitionId: itemDefinitionIdSchema,
  displayName: z.string().min(1),
  /**
   * One sentence about what this skin IS.
   *
   * It must not claim the skin has no effect. A skin instance carries socket
   * capacity, and sockets are for jewels that may grant attributes in the hunt
   * (ADR 0040) - so "purely cosmetic" would be a promise this product has
   * already decided not to keep. It equally must not promise an effect that
   * does not exist yet.
   */
  blurb: z.string().min(1),
  /**
   * Whether this definition is finished content that may be granted at all.
   *
   * Replaces `redeemable`, which asked whether a free claim was available - a
   * question the product stopped asking when ADR 0039 retired the claim. This
   * one is about the DEFINITION, not about the account: `false` means there is
   * no PNG, no `assetKey`, no VFX descriptor and no banner, so no copy of it
   * can be created by anything.
   *
   * A client may use it to grey a card out. It is never an offer: acquisition
   * is the Cash gacha's banner contract, and that is a different route.
   */
  enabled: z.boolean(),
  /**
   * Bound to the account, always, for every skin (ADR 0037).
   *
   * On the wire so the interface can say so without inferring it, and constant
   * today - which is the honest shape: it is a property of what a skin IS, not
   * of how this copy arrived.
   */
  soulbound: z.literal(true),
});
export type SkinView = z.infer<typeof skinViewSchema>;

/**
 * One COPY this account owns. Two copies of one definition are two entries.
 *
 * ## Why this exists at all
 *
 * Because a copy is the thing a player equips, and copies of the same skin are
 * not interchangeable: each is born with an immutable socket capacity of one to
 * six (ADR 0040), and rolling twice is only meaningful because the second copy
 * can be better than the first. A shape that keyed ownership by definition
 * threw the second copy away.
 */
export const ownedSkinSchema = z
  .object({
    /**
     * The opaque id of THIS copy. The only identity a client may use.
     *
     * Never a position in the array: the list is re-sorted whenever a copy is
     * gained, and an index would silently start meaning a different copy.
     */
    itemInstanceId: z.uuid(),
    /** Which art this copy is. Joins to `skinViewSchema.definitionId`. */
    definitionId: itemDefinitionIdSchema,
    /** Whether THIS copy is the one currently worn. At most one may be true. */
    equipped: z.boolean(),
    /**
     * How many sockets this copy was born with, or `null` when unknown.
     *
     * One to six, immutable, decided once when the copy was created and never
     * re-drawn - not on a read, an equip, an unequip, a reload or a reconnect.
     *
     * `null` is NOT "no sockets" and must never be drawn as zero. It means this
     * copy predates the roulette and has no detail row, so the honest answer is
     * that nobody knows. Inventing a capacity for those rows would be inventing
     * an item property out of nothing.
     */
    socketCapacity: z.number().int().min(1).max(6).nullable(),
  })
  .strict();
export type OwnedSkin = z.infer<typeof ownedSkinSchema>;

export const skinsResponseSchema = z.object({
  contractVersion: z.literal(SKINS_CONTRACT_VERSION),
  /** The catalogue: what each skin IS. One entry per definition. */
  skins: z.array(skinViewSchema),
  /**
   * The shelf: every copy this account holds, duplicates included.
   *
   * Ordered by `(definitionId, itemInstanceId)`. That is deterministic rather
   * than meaningful - it is not acquisition order, because `item_instances` has
   * no timestamp and the detail row that does have one is absent for copies
   * that predate the roulette. A stable order the client can rely on is worth
   * more than an order that is sometimes chronological and sometimes not.
   */
  owned: z.array(ownedSkinSchema),
  /** The version to send with the next equip or unequip. */
  loadoutVersion: z.number().int().min(0),
  /** The worn copy's instance id, or null. Redundant with `owned`, and cheap. */
  equippedSkinInstanceId: z.uuid().nullable(),
});
export type SkinsResponse = z.infer<typeof skinsResponseSchema>;

export const skinEquipRequestSchema = z
  .object({
    itemInstanceId: z.uuid(),
    /**
     * The loadout version the client believes it is changing.
     *
     * Optimistic concurrency in the open: two tabs that both act from the same
     * view carry the same number, and only the first can win.
     */
    expectedLoadoutVersion: z.number().int().min(0),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type SkinEquipRequest = z.infer<typeof skinEquipRequestSchema>;

export const skinUnequipRequestSchema = z
  .object({
    expectedLoadoutVersion: z.number().int().min(0),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type SkinUnequipRequest = z.infer<typeof skinUnequipRequestSchema>;

/**
 * The RUN the slot move reconfigured, as the client must adopt it (ADR 0042).
 *
 * ## Why a slot move answers with a hunt at all
 *
 * Because the transaction that moved the slot already produced this. It locks
 * the progression, locks the active session, settles the un-checkpointed tail
 * under the OLD loadout, moves the slot inside the vault's idempotent
 * envelope, and rewrites the SAME session's frozen loadout with the new
 * presentation identity. Every one of those facts is authoritative the instant
 * it commits.
 *
 * Answering with a shelf and letting the client fetch the run again would be a
 * second round trip, a second reconciliation and a second opportunity to
 * disagree - about a change the server had already finished making. The
 * expensive half is done; this is the server saying what it did.
 *
 * ## Strict, and the strictness is the leak guard
 *
 * The server parses its own answer through this schema before sending it, so
 * `.strict()` is not politeness towards clients: it is what makes
 * "`socketCapacity` cannot reach a browser" a property of the boundary rather
 * than of somebody's care. A field added upstream fails the route instead of
 * shipping. The same holds for a jewel, a seed, an origin, a banner, a price,
 * a Cash balance, a ledger row and an asset path - none of them appears in any
 * of the four members below, and none can be added without failing here.
 *
 * ## What is NOT here, and why
 *
 * No passives, no pet and no equipment totals. A skin changes none of them:
 * the session keeps the artifact totals, the pet and the components it froze,
 * and re-sending copies would invite a client to adopt values this operation
 * never recomputed. No settlement figures either - the tail's XP and Gold are
 * already inside `progression`, which is the confirmed total rather than a
 * delta somebody has to add up.
 */
export const skinHuntReconfigurationSchema = z
  .object({
    /**
     * The SAME session. Not a new one, and never a replacement.
     *
     * A skin change must not start, end or replace a run. The id here is the
     * id the client already holds; what moved is `lastSequence` and
     * `acceptedTickMs`, because settling the tail consumed a window.
     */
    session: huntSessionStateSchema,
    /** The confirmed progression after that settlement. Never a prediction. */
    progression: progressionSchema,
    /**
     * The session's own frozen loadout with the skin replaced.
     *
     * Ship and cannon are the ones the run was configured with - the operation
     * copies them across untouched - so damage, cadence, formation and hitbox
     * are the same numbers they were a moment ago. Only `skin` moved.
     */
    loadout: loadoutSchema,
    /**
     * The authoritative deterministic state to continue from.
     *
     * Typed as unknown on the wire exactly as the bootstrap's is: its real
     * contract is `parseHuntSnapshot` in `@kenocyber/game-domain`, which the
     * client runs before restoring. It is the snapshot the settlement wrote -
     * never a fresh export, which would advance the simulation over seconds
     * nobody played.
     */
    snapshot: z.unknown(),
  })
  .strict();
export type SkinHuntReconfiguration = z.infer<typeof skinHuntReconfigurationSchema>;

export const skinMutationResponseSchema = skinsResponseSchema.extend({
  replayed: z.boolean(),
  /**
   * The reconfigured run, or `null` when the account was not hunting.
   *
   * Null is the ordinary case and not a degraded one: changing a skin from a
   * menu must not start a hunt. The slot moves, the shelf comes back, and
   * there is no arena to tell about it.
   *
   * On a REPLAY this carries the run as it is NOW, recomputed from committed
   * state - never a snapshot stored during the first attempt. The replayed
   * answer is the one that may have been lost, so it has to be able to
   * reconfigure a client that never received the original.
   */
  hunt: skinHuntReconfigurationSchema.nullable(),
});
export type SkinMutationResponse = z.infer<typeof skinMutationResponseSchema>;
